/**
 * @module DialogClose
 *
 * @see https://0.vuetifyjs.com/components/disclosure/dialog
 *
 * @remarks
 * Close button component for dialogs. Closes the dialog when clicked.
 * Should be used inside DialogContent.
 */

<script lang="ts">
  // Types
  import type { AtomProps } from '#v0/components/Atom'

  export interface DialogCloseProps extends AtomProps {
    /** Namespace for dependency injection */
    namespace?: string
  }

  export interface DialogCloseSlotProps {
    /** Whether the dialog is currently open */
    isOpen: boolean
    /** Attributes to bind to the close button element */
    attrs: {
      'type': 'button' | undefined
      'role': 'button' | undefined
      'tabindex': number
      'aria-label': string
      'onClick': () => void
      'onKeydown': ((e: KeyboardEvent) => void) | undefined
    }
  }
</script>

<script setup lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Context
  import { useDialogContext } from './DialogRoot.vue'

  // Composables
  import { useLocale } from '#v0/composables/useLocale'

  // Utilities
  import { toRef } from 'vue'

  defineOptions({ name: 'DialogClose' })

  defineSlots<{
    default: (props: DialogCloseSlotProps) => any
  }>()

  const {
    as = 'button',
    namespace = 'v0:dialog',
    renderless,
  } = defineProps<DialogCloseProps>()

  const context = useDialogContext(namespace)
  const locale = useLocale()

  function onClick () {
    context.close()
  }

  function onKeydown (e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  }

  const slotProps = toRef((): DialogCloseSlotProps => ({
    isOpen: context.isOpen.value,
    attrs: {
      'type': as === 'button' ? 'button' : undefined,
      'role': as === 'button' ? undefined : 'button',
      'tabindex': 0,
      'aria-label': locale.ti('Dialog.close') ?? 'Close',
      'onClick': onClick,
      'onKeydown': as === 'button' ? undefined : onKeydown,
    },
  }))
</script>

<template>
  <Atom
    :as
    :renderless
    v-bind="slotProps.attrs"
  >
    <slot v-bind="slotProps" />
  </Atom>
</template>
